import { VALID_COMBO_CARD_TYPES } from '../constants/common-enums';
import getBillingCountries from './get-billing-countries';
import getCardBrands from './get-card-brands';
import getCardError from './get-card-error';
import getCardType from './get-card-type';
import StaticIdSingletonClass from './static-id';

/**
 * Returns JS Spec customised card list objects
 *  - transform Set Intent API reponse to cardList JS Adapter response structure.
 * @param {object} response
 * @return {array} card list in JS Adapter response format.
 */
const getCardListExternal = response => {
  const paymentInstrument = response?.data?.paymentInstrument;
  const merchantConfig = response?.data?.merchantConfig;
  const merchantCanadaVisaDebitSupport =
    merchantConfig?.paymentConstraints?.acceptCanadianVISADebit ?? true;
  // for partial billing address 'FULL' 'CNTRY' 'POSTALCNTRY'
  const merchantBillingAddressConstraints =
    merchantConfig?.billingConstraints?.collectBillingAddress ?? '';
  const merchantCountryCode = response?.merchantCountryCode ?? '';
  const merchantSupportedBillingCountries = getBillingCountries(response);
  const merchantSupportedCardBrands = getCardBrands(response);
  if (paymentInstrument) {
    const cardList = [];
    for (const card of paymentInstrument) {
      // TODO: check with BE for these keys
      const artUri = card?.cardArts?.cardArt?.[0]?.baseImageFileName ?? null;
      const artHeight = card?.cardArts?.cardArt?.[0]?.height ?? null;
      const artWidth = card?.cardArts?.cardArt?.[0]?.width ?? null;

      const type = card?.dcfs?.dcfInfo?.[0]?.walletType ?? null;
      // Per v0.53 spec, status is mapped against card.expired.
      // if expired is not present, we assume the card as non-expiring card.
      const expired = card?.expired ?? false;
      const status = expired ? 'EXPIRED' : 'ACTIVE';
      const addressId = card?.billingAddressId ?? null;
      const line1 = card?.billingAddress?.line1 ?? null;
      const line2 = card?.billingAddress?.line2 ?? null;
      const line3 = card?.billingAddress?.line3 ?? null;
      const city = card?.billingAddress?.city ?? null;
      const state = card?.billingAddress?.stateProvinceCode ?? null;
      const zip = card?.billingAddress?.postalCode ?? null;
      const countryCode = card?.billingAddress?.countryCode ?? null;

      // constructing cardInfo key object (cardInfo.digitalCardData, cardInfo.dcf, cardInfo.maskedBillingAddress)
      const dcf = { type };
      const maskedBillingAddress = {
        addressId,
        city,
        countryCode,
        line1,
        line2,
        line3,
        state,
        zip
      };
      const digitalCardData = {
        artHeight,
        artUri,
        artWidth,
        status
      };

      // get cardInfo keys from response
      const srcDigitalCardId = card?.digitalCardId ?? card?.id ?? null;
      const tokenId = card?.tokenId ?? null;
      const panBin = card?.binSixDigits ?? null;
      const panLastFour = card?.lastFourDigits ?? null;
      // TODO: check with BE for these keys
      const panExpirationMonth = card?.expirationDate?.month ?? null;
      const panExpirationYear = card?.expirationDate?.year ?? null;
      const creationTimestamp = card?.creationTimestamp ?? null;
      const dateOfCardCreated = creationTimestamp
        ? new Date(creationTimestamp).toISOString()
        : null;
      const lastUsedTimeStamp = card?.lastUsedTimeStamp ?? null;
      const isInstallmentEligible = Boolean(card?.isInstallmentEligible ?? false);
      const dateOfCardLastUsed = lastUsedTimeStamp
        ? new Date(lastUsedTimeStamp).toISOString()
        : null;
      const eligible = card?.accepted ?? null;
      // Card error
      const combinationCard = card?.combinationCard ?? null; // Expected values - A, C, N or null
      const isComboCard = VALID_COMBO_CARD_TYPES.includes(combinationCard);
      const data = {
        card,
        isComboCard,
        merchantBillingAddressConstraints,
        merchantCanadaVisaDebitSupport,
        merchantCountryCode,
        merchantSupportedBillingCountries,
        merchantSupportedCardBrands
      };
      const cardError = getCardError(data);
      // constructing cardInfo object
      const cardInfo = {
        cardError,
        dateOfCardCreated,
        dateOfCardLastUsed,
        dcf,
        digitalCardData,
        eligible,
        isInstallmentEligible,
        maskedBillingAddress,
        panBin,
        panExpirationMonth,
        panExpirationYear,
        panLastFour,
        srcDigitalCardId,
        tokenId
      };
      // Below cardType and network are not in JS Spec. Addl Info passed for Visa UI.
      if (StaticIdSingletonClass.visaSrci) {
        const ctype = card?.paymentType?.cardType ?? null;
        const networkId = card?.paymentType?.cardBrand ?? null;
        const cardType = getCardType(
          isComboCard,
          ctype,
          merchantSupportedCardBrands,
          merchantCountryCode
        );
        Object.assign(cardInfo, { cardType, networkId });
      }
      cardList.push(cardInfo);
    }
    return cardList;
  }
  return [];
};

export default getCardListExternal;
